import { useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia?.("(display-mode: standalone)").matches;
  // iOS Safari exposes this non-standard flag when launched from the home screen.
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return Boolean(mql || iosStandalone);
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isAppleMobile = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ reports as "MacIntel" with touch support.
  const isIpadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isAppleMobile || isIpadOS;
}

export default function InstallAppPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("vv-install-dismissed") === "1";
    } catch {
      return false;
    }
  });
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    const mql = window.matchMedia?.("(display-mode: standalone)");
    const onDisplayModeChange = () => setInstalled(isStandalone());

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    mql?.addEventListener?.("change", onDisplayModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      mql?.removeEventListener?.("change", onDisplayModeChange);
    };
  }, []);

  if (installed || dismissed) return null;

  const ios = isIOS();
  // Only show when we can actually do something useful: a real Android/desktop
  // install prompt, or iOS (where we show manual instructions).
  if (!deferredPrompt && !ios) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem("vv-install-dismissed", "1");
    } catch {
      /* ignore */
    }
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
      }
      setDeferredPrompt(null);
      return;
    }
    if (ios) {
      setShowIOSHelp(true);
    }
  };

  return (
    <>
      <div
        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-sm"
        role="region"
        aria-label="Instalar aplicación"
      >
        <div className="flex items-center gap-3 rounded-2xl bg-[#0B1330] border border-[#1e3260] shadow-xl px-4 py-3">
          <div className="w-9 h-9 rounded-xl bg-primary-500/15 flex items-center justify-center shrink-0">
            <i className="ri-download-2-line text-primary-400 text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold leading-tight">Instalar Vívelo AI</p>
            <p className="text-foreground-300 text-xs leading-tight">Acceso rápido desde tu pantalla de inicio</p>
          </div>
          <button
            type="button"
            onClick={handleInstallClick}
            className="shrink-0 px-3.5 py-2 rounded-xl bg-primary-500 hover:bg-primary-400 text-background-950 text-xs font-heading font-bold cursor-pointer whitespace-nowrap"
          >
            Instalar
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Cerrar"
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-white hover:bg-white/5 cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      </div>

      {showIOSHelp ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 px-4 pb-6"
          onClick={() => setShowIOSHelp(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-[#0B1330] border border-[#1e3260] p-5 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-white font-heading font-bold text-base mb-3">Instalar en iPhone</p>
            <ol className="text-left text-foreground-300 text-sm space-y-2 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-primary-400 font-bold">1.</span>
                <span>
                  Toca el ícono de compartir <i className="ri-share-forward-line"></i> en la barra de Safari.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-400 font-bold">2.</span>
                <span>Busca y selecciona "Añadir a pantalla de inicio".</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-400 font-bold">3.</span>
                <span>Toca "Añadir" arriba a la derecha.</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={() => setShowIOSHelp(false)}
              className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-background-950 font-heading font-bold text-sm cursor-pointer"
            >
              Entendido
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
